import { isFakeRoomId } from './chatHelpers';

// Чат тізімі мен соңғы ашылған чаттардың хабарламаларының localStorage кэші —
// тек алғашқы кадр үшін (бет жаңартылғанда серверді күтпей көрсету). Шындық
// көзі — сервер: REST/socket жауабы келгенде кэштегі нұсқа толығымен ауыстырылады.
const PREFIX = 'chatCache:';
const ROOMS_KEY = `${PREFIX}rooms`;
const INDEX_KEY = `${PREFIX}index`;
const roomKey = (roomId) => `${PREFIX}room:${roomId}`;
const VERSION = 1;
const MAX_MESSAGES = 50;
const MAX_ROOMS = 20;
const SAVE_ROOMS_DEBOUNCE_MS = 1000;

let saveRoomsTimer = null;

// Шыққаннан кейін (cleanup, debounce) кэш қайта жазылмасын
const isLoggedIn = () => !!localStorage.getItem('access_token');

const read = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data?.v !== VERSION && !Array.isArray(data)) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    try { localStorage.removeItem(key); } catch { /* storage қолжетімсіз */ }
    return null;
  }
};

const readIndex = () => {
  const index = read(INDEX_KEY);
  return Array.isArray(index) ? index.map(String) : [];
};

const writeIndex = (index) => {
  localStorage.setItem(INDEX_KEY, JSON.stringify(index));
};

// Квота толса — ең ескі чат кілттерін бір-бірден өшіріп қайталаймыз;
// өшіретін ештеңе қалмаса — кэшсіз жалғастырамыз.
const write = (key, value) => {
  const json = JSON.stringify(value);
  for (;;) {
    try {
      localStorage.setItem(key, json);
      return true;
    } catch {
      const index = readIndex();
      const oldest = index.find(id => roomKey(id) !== key);
      if (oldest == null) return false;
      try {
        localStorage.removeItem(roomKey(oldest));
        writeIndex(index.filter(id => id !== oldest));
      } catch {
        return false;
      }
    }
  }
};

const withoutOnline = (chat) => ({ ...chat, is_online: false });

// Тек сервер растаған хабарламалар (id бар) — оптимистік/жіберілмеген сақталмайды.
const isConfirmed = (msg) => !!msg && (msg.message_id != null || msg.id != null) && !msg.sendError;

// Сервер эхосы оптимистік хабарламамен біріктірілгенде blob превьюі қалып қояды —
// қайта жүктегенде ол жарамсыз, сондықтан өрісті алып тастаймыз.
const stripBlobUrls = (msg) => {
  const clean = {};
  Object.entries(msg).forEach(([k, v]) => {
    if (!(typeof v === 'string' && v.startsWith('blob:'))) clean[k] = v;
  });
  return clean;
};

export const chatCache = {
  readRooms() {
    const data = read(ROOMS_KEY);
    if (!data || !Array.isArray(data.active)) return null;
    return {
      active: data.active.map(withoutOnline),
      archived: Array.isArray(data.archived) ? data.archived.map(withoutOnline) : [],
    };
  },

  saveRooms(active, archived) {
    clearTimeout(saveRoomsTimer);
    saveRoomsTimer = setTimeout(() => {
      if (!isLoggedIn()) return;
      write(ROOMS_KEY, { v: VERSION, active: active || [], archived: archived || [] });
    }, SAVE_ROOMS_DEBOUNCE_MS);
  },

  readMessages(roomId) {
    if (roomId == null || isFakeRoomId(roomId)) return [];
    const data = read(roomKey(roomId));
    return Array.isArray(data?.messages) ? data.messages : [];
  },

  saveMessages(roomId, messages) {
    if (roomId == null || isFakeRoomId(roomId) || !isLoggedIn()) return;
    const id = String(roomId);
    try {
      const index = [id, ...readIndex().filter(x => x !== id)];
      index.slice(MAX_ROOMS).forEach(old => localStorage.removeItem(roomKey(old)));
      writeIndex(index.slice(0, MAX_ROOMS));
    } catch { /* индекс жазылмаса да хабарламаны сақтап көреміз */ }
    const confirmed = (messages || []).filter(isConfirmed).slice(-MAX_MESSAGES).map(stripBlobUrls);
    write(roomKey(id), { v: VERSION, messages: confirmed });
  },

  removeRoom(roomId) {
    if (roomId == null) return;
    const id = String(roomId);
    try {
      localStorage.removeItem(roomKey(id));
      writeIndex(readIndex().filter(x => x !== id));
    } catch { /* storage қолжетімсіз */ }
  },

  clearAll() {
    clearTimeout(saveRoomsTimer);
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(PREFIX)) localStorage.removeItem(k);
      }
    } catch { /* storage қолжетімсіз */ }
  },
};
